import { Box, SxProps, Theme, useTheme } from "@mui/material";
import {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
  LazyTable,
  RoundedBox,
} from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  ListRequestOfUserFilter,
  UserFilter,
  UserModel,
} from "services/organizationPortalClient";
import contextStore from "stores/contextStore";
import PendingInvitationsBanner from "./PendingInvitationsBanner";
import StatusChip from "./StatusChip";
import ChangeAccessLevelDialog from "./ThreeDotsActions/ChangeAccessLevel/ChangeAccessLevelDialog";
import DeactivateUserDialog from "./ThreeDotsActions/DeactivateUser/DeactivateUserDialog";
import ReactivateUserDialog from "./ThreeDotsActions/ReactivateUser/ReactivateUserDialog";
import ResendInviteDialog from "./ThreeDotsActions/ResendInvite/ResendInviteDialog";
import UserThreeDotsMenu from "./ThreeDotsActions/UserThreeDotsMenu";
import UserActions from "./UserActions";
import UserPageFilter from "./UserPageFilter";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  title: {
    fontSize: "1.2em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    mb: 2,
  },
});

export default function UsersPage() {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { organizationUserService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("lastName");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filter, setFilter] = useState({} as UserFilter);
  const { organization } = contextStore.useState();

  const [userIdToDeactivate, setUserIdToDeactivate] = useState<string>();
  const [userIdToReactivate, setUserIdToReactivate] = useState<string>();
  const [userIdToResendInvite, setUserIdToResendInvite] = useState<string>();
  const [userToChangeAccessLevel, setUserToChangeAccessLevel] =
    useState<UserModel>();

  const { data, isFetching, refetch } = useQuery(
    [
      "organizationUserService.getUsers",
      organization?.id,
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
    ],
    () =>
      organizationUserService.getUsers(
        ListRequestOfUserFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: filter,
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const ProfessionView = ({ value }: { value: UserModel }) => {
    return <span>{value.professions?.map((p) => p.name).join(", ")}</span>;
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "firstName",
      type: "text",
      label: t({ id: "firstName" }),
    },
    {
      accessor: "lastName",
      type: "text",
      label: t({ id: "lastName" }),
    },
    {
      accessor: "email",
      type: "text",
      label: t({ id: "email" }),
    },
    {
      accessor: "",
      type: "node",
      noSort: true,
      label: t({ id: "profession" }),
      View: ProfessionView,
    },
    {
      accessor: "accessLevelName",
      type: "text",
      noSort: true,
      label: t({ id: "levelOfAccess" }),
    },
    {
      accessor: "statusTypeName",
      type: "node",
      noSort: true,
      label: t({ id: "status" }),
      View: StatusChip,
    },
    {
      accessor: "",
      type: "node",
      label: "",
      noSort: true,
      View: ({ value }: { value: UserModel }) => (
        <UserThreeDotsMenu
          user={value}
          deactivate={setUserIdToDeactivate}
          reactivate={setUserIdToReactivate}
          resendInvite={setUserIdToResendInvite}
          changeAccessLevel={setUserToChangeAccessLevel}
        />
      ),
    },
  ];

  return (
    <Box>
      <Box sx={styles.title}>
        <T id="users" />
      </Box>
      <RoundedBox>
        <UserPageFilter onChange={setFilter} />
      </RoundedBox>
      <PendingInvitationsBanner />
      <RoundedBox>
        <LazyTable
          {...{
            "aria-label": t({ id: "users" }),
            loading: isFetching,
            rowCount: data?.totalRecords ?? 0,
            rows: data?.data ?? [],
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            titleComponent: (
              <UserActions
                sortDescending={order === "desc"}
                sortField={orderBy}
                userFilter={filter}
                refetch={refetch}
              />
            ),
          }}
        />
      </RoundedBox>

      <DeactivateUserDialog
        organizationUserId={userIdToDeactivate}
        close={() => setUserIdToDeactivate(undefined)}
      />
      <ReactivateUserDialog
        organizationUserId={userIdToReactivate}
        close={() => setUserIdToReactivate(undefined)}
      />
      <ResendInviteDialog
        organizationUserId={userIdToResendInvite}
        close={() => setUserIdToResendInvite(undefined)}
      />
      <ChangeAccessLevelDialog
        organizationUser={userToChangeAccessLevel}
        onClose={() => setUserToChangeAccessLevel(undefined)}
      />
    </Box>
  );
}
